import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';

/**
 * ActionMenu — the "⋯" menu for the row / card actions that do not fit inline (spec §3.5, §3.16):
 * a round 36px ghost icon button that opens a `.ui-menu` card. Rows keep at most two inline
 * actions; the rest (Tahrirlash, Nashrdan olish, O'chirish …) live here.
 *
 *   <ActionMenu
 *     ariaLabel="Kurs amallari"
 *     items={[
 *       { key: 'edit', label: 'Tahrirlash', icon: <Pencil />, onSelect: edit },
 *       { key: 'unpublish', label: 'Nashrdan olish', onSelect: unpublish },
 *       { key: 'delete', label: "O'chirish", icon: <Trash2 />, tone: 'danger', onSelect: remove },
 *     ]}
 *   />
 *
 * items: [{ key, label, icon?, onSelect, tone?: 'danger', disabled?, separator?: true }] — a
 * `separator` item draws a hairline instead. The click that opens it never bubbles to a
 * clickable row or card behind it. Keyboard: Enter / Space / Arrow Down opens, arrows move,
 * Enter picks, Esc closes (and never also closes a dialog under it).
 */
function ActionMenu({ items = [], ariaLabel, icon, className = '', align = 'end', disabled = false }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);
  const pos = useAnchoredPopover(open, setOpen, anchorRef, popRef, { align });

  const actionable = items.map((item, index) => ({ item, index })).filter(({ item }) => !item.separator && !item.disabled);

  const close = useCallback(() => setOpen(false), []);

  const openMenu = (e) => {
    e.stopPropagation(); // a row or card behind the button must not also react
    if (disabled) return;
    if (open) { close(); return; }
    setActive(actionable.length ? actionable[0].index : 0);
    setOpen(true);
  };

  const choose = (item) => {
    close();
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
    if (item.onSelect) item.onSelect(item);
  };

  const positioned = Boolean(pos);
  useLayoutEffect(() => {
    if (open && positioned && listRef.current) listRef.current.focus({ preventScroll: true });
  }, [open, positioned]);

  const move = (delta) => {
    if (actionable.length === 0) return;
    const at = actionable.findIndex(({ index }) => index === active);
    const next = actionable[(at + delta + actionable.length) % actionable.length];
    setActive(next.index);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Home') { e.preventDefault(); if (actionable.length) setActive(actionable[0].index); }
    else if (e.key === 'End') { e.preventDefault(); if (actionable.length) setActive(actionable[actionable.length - 1].index); }
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const target = items[active];
      if (target && !target.separator && !target.disabled) choose(target);
    } else if (e.key === 'Tab') close();
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' && !open) openMenu(e);
  };

  const menu = open ? (
    <div
      ref={popRef}
      className="ui-menu ui-action-menu"
      style={pos ? { top: pos.top, left: pos.left, zIndex: pos.zIndex } : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
      onClick={(e) => e.stopPropagation()}
    >
      <ul
        ref={listRef}
        className="ui-menu__list"
        role="menu"
        tabIndex={-1}
        aria-label={ariaLabel}
        aria-activedescendant={items[active] && !items[active].separator ? `${uid}-${active}` : undefined}
        onKeyDown={onKeyDown}
      >
        {items.map((item, i) => (item.separator ? (
          <li key={item.key || `sep-${i}`} role="separator" className="ui-menu__sep" />
        ) : (
          <li
            key={item.key || item.label}
            id={`${uid}-${i}`}
            role="menuitem"
            aria-disabled={item.disabled || undefined}
            data-tone={item.tone}
            className={`ui-menu__item${i === active ? ' is-focused' : ''}${item.disabled ? ' is-disabled' : ''}`}
            onMouseMove={() => { if (!item.disabled && active !== i) setActive(i); }}
            onClick={() => { if (!item.disabled) choose(item); }}
          >
            {item.icon && <span className="ui-menu__icon" aria-hidden="true">{item.icon}</span>}
            <span>{item.label}</span>
          </li>
        )))}
      </ul>
    </div>
  ) : null;

  return (
    <div ref={anchorRef} className={`ui-action-menu-anchor${className ? ` ${className}` : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className="ui-icon-btn"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={openMenu}
        onKeyDown={onTriggerKeyDown}
      >
        {icon || <MoreHorizontal aria-hidden="true" />}
      </button>
      {menu && createPortal(menu, popoverRoot(anchorRef.current))}
    </div>
  );
}

export default ActionMenu;
